import { lazy, type LazyExoticComponent, type ComponentType } from 'react';

/** `object` keeps ESLint happy while staying compatible with `FC` / routed pages with props. */
type Importer<T extends ComponentType<object>> = () => Promise<{ default: T }>;

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Wrap `React.lazy` to recover from transient chunk-load issues (stale index.html / partial deploy).
 * Strategy: retry once, then force a full reload (which fetches fresh index.html + asset map).
 */
export function lazyWithRetry<T extends ComponentType<object>>(
  importer: Importer<T>,
  opts?: { retries?: number; retryDelayMs?: number }
): LazyExoticComponent<T> {
  const retries = opts?.retries ?? 1;
  const retryDelayMs = opts?.retryDelayMs ?? 250;

  return lazy(async () => {
    for (let i = 0; i <= retries; i++) {
      try {
        return await importer();
      } catch (err) {
        if (i === retries) {
          // Best-effort recovery: reload the app to get a consistent set of assets.
          console.error('[lazyWithRetry] import failed; reloading', err);
          window.location.reload();
          throw err;
        }
        await wait(retryDelayMs);
      }
    }
    throw new Error('[lazyWithRetry] unreachable');
  });
}

